import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import { detailedReportService } from "../services/detailedReportService";
import api from "../services/api";
import OverviewReports from "./Reports"; // The old reports page kept for the Quick Overview tab
import {
  BarChart3, DollarSign, AlertCircle, TrendingUp, Users,
  FileSpreadsheet, MapPin, Download, Search, ChevronRight, Building2,
  CalendarRange, FileEdit, Workflow, UserCheck, Timer, PhoneCall, UserPlus,
  Filter, Hammer, Activity, ShieldAlert,
} from "lucide-react";

// Format currency
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

// ============================================================================
// Report registry — add new reports here
// ============================================================================
const REPORTS = [
  {
    id: "overview",
    name: "Quick Overview",
    description: "At-a-glance dashboard of revenue, projects, leads, customers, and costs",
    icon: BarChart3,
    category: "Overview",
    component: () => <OverviewReports />,
  },
  {
    id: "pnl-by-project",
    name: "Profit & Loss by Project",
    description: "Revenue vs costs per project with margin %, exportable to Excel",
    icon: DollarSign,
    category: "Financial",
    component: PnLByProjectReport,
  },
  {
    id: "ar-aging",
    name: "A/R Aging",
    description: "Outstanding payments grouped by 0-30 / 31-60 / 61-90 / 90+ days overdue",
    icon: AlertCircle,
    category: "Financial",
    component: ARAgingReport,
  },
  {
    id: "revenue",
    name: "Revenue Report",
    description: "Every payment received, filtered by date",
    icon: TrendingUp,
    category: "Financial",
    component: RevenueReport,
  },
  {
    id: "vendor-spend",
    name: "Vendor & Sub Spend",
    description: "Total spent at each vendor / subcontractor with line-item detail",
    icon: Building2,
    category: "Financial",
    component: VendorSpendReport,
  },
  {
    id: "expected-revenue",
    name: "Expected Revenue",
    description: "Projects starting or ending in a date range with contract value",
    icon: CalendarRange,
    category: "Financial",
    component: ExpectedRevenueReport,
  },
  {
    id: "change-orders",
    name: "Change Orders",
    description: "All change orders across all projects with amounts and dates",
    icon: FileEdit,
    category: "Financial",
    component: ChangeOrdersReport,
  },
  {
    id: "lead-source-roi",
    name: "Lead Source ROI",
    description: "Conversion rate and revenue by lead source",
    icon: Users,
    category: "Sales",
    component: LeadSourceROIReport,
  },
  {
    id: "sales-pipeline",
    name: "Sales Pipeline",
    description: "All active projects bucketed by status with $ at each stage",
    icon: Workflow,
    category: "Sales",
    component: SalesPipelineReport,
  },
  {
    id: "conversion-funnel",
    name: "Conversion Funnel",
    description: "Lead → Customer → Project win rates with drop-off at each stage",
    icon: Filter,
    category: "Sales",
    component: ConversionFunnelReport,
  },
  {
    id: "customer-ltv",
    name: "Customer Lifetime Value",
    description: "Total revenue per customer, ranked",
    icon: UserCheck,
    category: "Customers",
    component: CustomerLTVReport,
  },
  {
    id: "customer-acquisition",
    name: "New Customer Acquisition",
    description: "New customers per period grouped by source and month",
    icon: UserPlus,
    category: "Customers",
    component: CustomerAcquisitionReport,
  },
  {
    id: "project-duration",
    name: "Project Duration",
    description: "Days from contract to start to completion, by project",
    icon: Timer,
    category: "Operations",
    component: ProjectDurationReport,
  },
  {
    id: "callbacks-analysis",
    name: "Callbacks Analysis",
    description: "Callbacks by urgency, resolution time, and crew hours",
    icon: PhoneCall,
    category: "Operations",
    component: CallbacksAnalysisReport,
  },
  {
    id: "crew-utilization",
    name: "Crew Utilization",
    description: "Per-crew callback hours, project assignments, and payroll value",
    icon: Hammer,
    category: "Operations",
    component: CrewUtilizationReport,
  },
  {
    id: "user-activity",
    name: "User Activity",
    description: "Per-user task and email activity, project assignments",
    icon: Activity,
    category: "Operations",
    component: UserActivityReport,
  },
  {
    id: "payment-audit",
    name: "Payment Audit",
    description: "Find paid payments where the current amount differs from the receipt that was sent (detects past data corruption)",
    icon: ShieldAlert,
    category: "Operations",
    component: PaymentAuditReport,
  },
  {
    id: "jobs-by-location",
    name: "Jobs by Location",
    description: "Filter projects by city or ZIP code, with rollups",
    icon: MapPin,
    category: "Geographic",
    component: JobsByLocationReport,
  },
  {
    id: "proximity-search",
    name: "Proximity Search",
    description: "Find all projects/customers/leads within X miles of any address",
    icon: Search,
    category: "Geographic",
    component: ProximitySearchReport,
  },
  {
    id: "map-view",
    name: "Map View",
    description: "Plot all leads, customers, or projects on a Google Map",
    icon: MapPin,
    category: "Geographic",
    component: MapViewReport,
  },
];

export default function ReportsNew() {
  const [activeId, setActiveId] = useState("overview");
  const active = REPORTS.find(r => r.id === activeId);
  const grouped = REPORTS.reduce((acc, r) => {
    acc[r.category] = acc[r.category] || [];
    acc[r.category].push(r);
    return acc;
  }, {});

  return (
    <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 p-4 sm:p-6 min-h-screen">
      {/* Sidebar */}
      <aside className="lg:w-72 shrink-0 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Choose a report</p>
        </div>
        {Object.entries(grouped).map(([category, reports]) => (
          <div key={category}>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{category}</div>
            <div className="space-y-1">
              {reports.map((r) => {
                const Icon = r.icon;
                const isActive = activeId === r.id;
                return (
                  <button
                    key={r.id}
                    disabled={r.comingSoon}
                    onClick={() => !r.comingSoon && setActiveId(r.id)}
                    className={`w-full text-left rounded-md px-3 py-2 flex items-start gap-3 transition-colors ${
                      isActive ? "bg-brand-50 text-brand-700"
                      : r.comingSoon ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${isActive ? "text-brand-600" : ""}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {r.name}
                        {r.comingSoon && <Badge variant="outline" className="text-[10px]">Soon</Badge>}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{r.description}</div>
                    </div>
                    {isActive && <ChevronRight className="h-4 w-4 text-brand-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {active && !active.comingSoon ? <active.component /> : null}
      </main>
    </div>
  );
}

// ============================================================================
// Shared sort hook + arrow renderer
// ============================================================================
function useSortedRows(rawRows, defaultCol, defaultDir = "desc") {
  const [sortBy, setSortBy] = useState(defaultCol);
  const [sortDir, setSortDir] = useState(defaultDir);

  const rows = React.useMemo(() => {
    const arr = [...(rawRows || [])];
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (r) => {
      const v = r[sortBy];
      if (v === null || v === undefined) return sortDir === "asc" ? Infinity : -Infinity;
      if (typeof v === "string") return v.toLowerCase();
      return Number(v);
    };
    arr.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [rawRows, sortBy, sortDir]);

  const onSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortArrow = ({ col }) => {
    if (sortBy !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-brand-600 ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  return { rows, onSort, SortArrow, sortBy, sortDir };
}

// ============================================================================
// Shared filter components
// ============================================================================
function DateRangeFilter({ dateFrom, dateTo, setDateFrom, setDateTo }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <Label className="text-xs">From</Label>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-44" />
      </div>
      <div>
        <Label className="text-xs">To</Label>
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-44" />
      </div>
      {(dateFrom || dateTo) && (
        <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }}>
          Clear
        </Button>
      )}
    </div>
  );
}

function ExportButton({ onClick, loading }) {
  return (
    <Button onClick={onClick} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
      <Download className="h-4 w-4 mr-2" />
      {loading ? "Exporting..." : "Export to Excel"}
    </Button>
  );
}

// ============================================================================
// 1. Profit & Loss by Project
// ============================================================================
function PnLByProjectReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["report-pnl", dateFrom, dateTo],
    queryFn: () => detailedReportService.pnlByProject({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const totals = data?.data?.totals || {};
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "gross_profit", "desc");

  const handleExport = async () => {
    setExporting(true);
    try {
      await detailedReportService.pnlByProjectExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined });
    } catch (e) {
      toast({ variant: "destructive", description: e.message || "Export failed" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5" /> Profit & Loss by Project</CardTitle>
          <CardDescription>Revenue and costs per project. Leave dates blank for all-time.</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard label="Projects" value={totals.project_count || 0} />
          <SummaryCard label="Revenue" value={fmt(totals.revenue)} color="text-green-600" />
          <SummaryCard label="Total Costs" value={fmt(totals.total_costs)} color="text-red-600" />
          <SummaryCard label="Gross Profit" value={fmt(totals.gross_profit)} color={totals.gross_profit >= 0 ? "text-green-600" : "text-red-600"} />
          <SummaryCard label="Margin" value={fmtPct(totals.profit_margin)} />
        </div>

        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="status" onSort={onSort} arrow={<SortArrow col="status" />}>Status</SortableTh>
                  <SortableTh col="assigned_users" onSort={onSort} arrow={<SortArrow col="assigned_users" />}>Assigned Users</SortableTh>
                  <SortableTh col="crews" onSort={onSort} arrow={<SortArrow col="crews" />}>Crews/Subs</SortableTh>
                  <SortableTh col="contract_price" onSort={onSort} arrow={<SortArrow col="contract_price" />} align="right">Contract</SortableTh>
                  <SortableTh col="revenue_in_period" onSort={onSort} arrow={<SortArrow col="revenue_in_period" />} align="right">Revenue</SortableTh>
                  <SortableTh col="materials_cost" onSort={onSort} arrow={<SortArrow col="materials_cost" />} align="right">Materials</SortableTh>
                  <SortableTh col="receipts_cost" onSort={onSort} arrow={<SortArrow col="receipts_cost" />} align="right">Receipts</SortableTh>
                  <SortableTh col="total_costs" onSort={onSort} arrow={<SortArrow col="total_costs" />} align="right">Costs</SortableTh>
                  <SortableTh col="gross_profit" onSort={onSort} arrow={<SortArrow col="gross_profit" />} align="right">Profit</SortableTh>
                  <SortableTh col="profit_margin" onSort={onSort} arrow={<SortArrow col="profit_margin" />} align="right">Margin</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.project_id} className="hover:bg-gray-50">
                    <Td>{r.project_name}</Td>
                    <Td>{r.customer_name}</Td>
                    <Td><Badge variant="outline" className="text-xs">{r.status}</Badge></Td>
                    <Td className="text-xs">{r.assigned_users || <span className="text-gray-400">—</span>}</Td>
                    <Td className="text-xs">{r.crews || <span className="text-gray-400">—</span>}</Td>
                    <Td align="right">{fmt(r.contract_price)}</Td>
                    <Td align="right" className="text-green-700">{fmt(r.revenue_in_period)}</Td>
                    <Td align="right" className="text-red-700">{fmt(r.materials_cost)}</Td>
                    <Td align="right" className="text-red-700">{fmt(r.receipts_cost)}</Td>
                    <Td align="right" className="text-red-700">{fmt(r.total_costs)}</Td>
                    <Td align="right" className={r.gross_profit >= 0 ? "text-green-700 font-semibold" : "text-red-700 font-semibold"}>{fmt(r.gross_profit)}</Td>
                    <Td align="right">{fmtPct(r.profit_margin)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 2. A/R Aging
// ============================================================================
function ARAgingReport() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-ar-aging"],
    queryFn: () => detailedReportService.arAging(),
  });
  const buckets = data?.data?.buckets || {};
  const summary = data?.data?.summary || {};
  const labels = [
    ["current", "Not Yet Due", "text-blue-700 bg-blue-50"],
    ["1_30", "1-30 days overdue", "text-yellow-700 bg-yellow-50"],
    ["31_60", "31-60 days overdue", "text-orange-700 bg-orange-50"],
    ["61_90", "61-90 days overdue", "text-red-700 bg-red-50"],
    ["over_90", "90+ days overdue", "text-red-900 bg-red-100"],
  ];

  // Flatten into one list so we can sort across buckets
  const allRows = React.useMemo(
    () => labels.flatMap(([key, label]) => (buckets[key] || []).map((item) => ({ ...item, bucket_label: label, bucket_key: key }))),
    [buckets]
  );
  const { rows, onSort, SortArrow } = useSortedRows(allRows, "days_overdue", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.arAgingExport(); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5" /> A/R Aging Report</CardTitle>
          <CardDescription>Outstanding payments grouped by how long they've been due</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {labels.map(([key, label, color]) => (
            <div key={key} className={`rounded-lg p-3 ${color}`}>
              <div className="text-xs font-medium">{label}</div>
              <div className="text-xl font-bold mt-1">{fmt(summary[key])}</div>
              <div className="text-xs opacity-75 mt-1">{(buckets[key] || []).length} items</div>
            </div>
          ))}
        </div>
        <div className="text-right text-lg font-semibold">Total Outstanding: <span className="text-red-700">{fmt(summary.total)}</span></div>

        {isLoading ? <Loading /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="bucket_label" onSort={onSort} arrow={<SortArrow col="bucket_label" />}>Bucket</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="customer_email" onSort={onSort} arrow={<SortArrow col="customer_email" />}>Email</SortableTh>
                  <SortableTh col="customer_phone" onSort={onSort} arrow={<SortArrow col="customer_phone" />}>Phone</SortableTh>
                  <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                  <SortableTh col="description" onSort={onSort} arrow={<SortArrow col="description" />}>Payment</SortableTh>
                  <SortableTh col="due_date" onSort={onSort} arrow={<SortArrow col="due_date" />}>Due</SortableTh>
                  <SortableTh col="days_overdue" onSort={onSort} arrow={<SortArrow col="days_overdue" />} align="right">Days Overdue</SortableTh>
                  <SortableTh col="amount" onSort={onSort} arrow={<SortArrow col="amount" />} align="right">Amount</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((item, i) => (
                  <tr key={`${item.bucket_key}-${i}`} className="hover:bg-gray-50">
                    <Td><span className="text-xs font-medium">{item.bucket_label}</span></Td>
                    <Td>{item.customer_name}</Td>
                    <Td className="text-xs">{item.customer_email}</Td>
                    <Td className="text-xs">{item.customer_phone}</Td>
                    <Td>{item.project_name}</Td>
                    <Td className="text-xs">{item.description}</Td>
                    <Td className="text-xs">{item.due_date}</Td>
                    <Td align="right">{item.days_overdue}</Td>
                    <Td align="right" className="font-medium">{fmt(item.amount)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 3. Revenue Report
// ============================================================================
function RevenueReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["report-revenue", dateFrom, dateTo],
    queryFn: () => detailedReportService.revenue({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const total = data?.data?.total || 0;
  const count = data?.data?.count || 0;
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "paid_date", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.revenueExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Revenue Report</CardTitle>
          <CardDescription>Every payment received. Leave dates blank for all-time.</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
        <div className="grid grid-cols-2 gap-3">
          <SummaryCard label="Payments" value={count} />
          <SummaryCard label="Total Revenue" value={fmt(total)} color="text-green-600" />
        </div>
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="paid_date" onSort={onSort} arrow={<SortArrow col="paid_date" />}>Paid Date</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                  <SortableTh col="payment_number" onSort={onSort} arrow={<SortArrow col="payment_number" />}>Payment #</SortableTh>
                  <SortableTh col="description" onSort={onSort} arrow={<SortArrow col="description" />}>Description</SortableTh>
                  <SortableTh col="amount" onSort={onSort} arrow={<SortArrow col="amount" />} align="right">Amount</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td>{r.paid_date}</Td><Td>{r.customer_name}</Td><Td>{r.project_name}</Td>
                    <Td>{r.payment_number}</Td><Td>{r.description}</Td>
                    <Td align="right" className="font-medium text-green-700">{fmt(r.amount)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 4. Vendor & Sub Spend
// ============================================================================
function VendorSpendReport() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState("summary");
  const [drilldown, setDrilldown] = useState(null); // { entity_name, projects }
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [costExact, setCostExact] = useState("");
  const [costMin, setCostMin] = useState("");
  const [costMax, setCostMax] = useState("");
  // Edit line item state
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingItem, setSavingItem] = useState(false);

  // Lookups for the edit dialog dropdowns
  const { data: projectsList } = useQuery({
    queryKey: ["vs-projects-list"],
    queryFn: () => api.get('/projects/', { params: { limit: 1000 } }),
  });
  const { data: vendorsList } = useQuery({ queryKey: ["vs-vendors"], queryFn: () => api.get('/vendors/') });
  const { data: subsList = null } = useQuery({ queryKey: ["vs-subs"], queryFn: () => api.get('/subcontractors/') });
  const { data: designersList = null } = useQuery({ queryKey: ["vs-designers"], queryFn: () => api.get('/designers/') });

  const openEditItem = (item) => {
    setEditingItem(item);
    setEditForm({
      item_name: item.item || "",
      cost: item.cost ?? "",
      vendorValue: item.vendor_id ? `${item.entity_type || 'vendor'}:${item.vendor_id}` : "",
      invoice_number: item.invoice_number || "",
      date: item.date ? String(item.date).slice(0, 10) : "",
      target_project_id: item.project_id,
    });
  };

  const handleSaveItem = async () => {
    if (!editingItem || !editForm) return;
    setSavingItem(true);
    try {
      let vendor_id = null;
      if (editForm.vendorValue) {
        vendor_id = editForm.vendorValue.split(":").slice(1).join(":") || null;
      }
      await detailedReportService.editCostItem({
        source_project_id: editingItem.project_id,
        kind: editingItem.kind,
        index: editingItem.index,
        target_project_id: editForm.target_project_id || editingItem.project_id,
        item_name: editForm.item_name,
        cost: editForm.cost === "" ? 0 : parseFloat(editForm.cost),
        vendor_id,
        invoice_number: editForm.invoice_number,
        date: editForm.date || null,
      });
      toast({ description: "Line item updated successfully" });
      setEditingItem(null);
      setEditForm(null);
      queryClient.invalidateQueries({ queryKey: ["report-vendor-spend"] });
    } catch (e) {
      toast({ variant: "destructive", description: e?.detail || e?.message || "Failed to update line item" });
    } finally {
      setSavingItem(false);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ["report-vendor-spend", dateFrom, dateTo],
    queryFn: () => detailedReportService.vendorSpend({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRollup = data?.data?.rollup || [];
  const rawLineItemsAll = data?.data?.line_items || [];
  // Apply client-side filters to line items
  const rawLineItems = React.useMemo(() => {
    let result = rawLineItemsAll;
    const q = invoiceSearch.trim().toLowerCase();
    if (q) result = result.filter(r => (r.invoice_number || "").toLowerCase().includes(q));
    const exact = costExact === "" ? null : parseFloat(costExact);
    if (exact !== null && !isNaN(exact)) {
      // Exact match (within 1 cent) — overrides min/max
      result = result.filter(r => Math.abs((parseFloat(r.cost) || 0) - exact) < 0.01);
    } else {
      const min = costMin === "" ? null : parseFloat(costMin);
      const max = costMax === "" ? null : parseFloat(costMax);
      if (min !== null && !isNaN(min)) result = result.filter(r => (parseFloat(r.cost) || 0) >= min);
      if (max !== null && !isNaN(max)) result = result.filter(r => (parseFloat(r.cost) || 0) <= max);
    }
    return result;
  }, [rawLineItemsAll, invoiceSearch, costExact, costMin, costMax]);
  const grandTotal = data?.data?.grand_total || 0;
  const filteredTotal = rawLineItems.reduce((s, r) => s + (parseFloat(r.cost) || 0), 0);
  const rollupSort = useSortedRows(rawRollup, "total_spent", "desc");
  const detailSort = useSortedRows(rawLineItems, "entity_name", "asc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.vendorSpendExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" /> Vendor & Subcontractor Spend</CardTitle>
          <CardDescription>How much you've spent at each vendor and subcontractor</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3 justify-between">
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
          <div className="flex gap-1 border rounded p-1">
            <button onClick={() => setView("summary")} className={`px-3 py-1 text-sm rounded ${view === "summary" ? "bg-brand-600 text-white" : ""}`}>Summary</button>
            <button onClick={() => setView("detail")} className={`px-3 py-1 text-sm rounded ${view === "detail" ? "bg-brand-600 text-white" : ""}`}>Line Items</button>
          </div>
        </div>
        {view === "detail" && (
          <div className="border rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-4">
                <Label className="text-xs">Invoice # contains</Label>
                <Input
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  placeholder="e.g. JPM99c, 4568"
                  className="h-9"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Exact cost $</Label>
                <Input type="number" value={costExact} onChange={(e) => setCostExact(e.target.value)} placeholder="e.g. 1500" className="h-9" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Min cost $</Label>
                <Input type="number" value={costMin} onChange={(e) => setCostMin(e.target.value)} placeholder="0.00" disabled={!!costExact} className="h-9" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Max cost $</Label>
                <Input type="number" value={costMax} onChange={(e) => setCostMax(e.target.value)} placeholder="no limit" disabled={!!costExact} className="h-9" />
              </div>
              <div className="md:col-span-1">
                {(invoiceSearch || costExact || costMin || costMax) && (
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => { setInvoiceSearch(""); setCostExact(""); setCostMin(""); setCostMax(""); }}>Clear</Button>
                )}
              </div>
            </div>
            <div className="text-xs text-gray-500 flex justify-between">
              <span>
                {(invoiceSearch || costExact || costMin || costMax) ? `Showing ${rawLineItems.length} of ${rawLineItemsAll.length}` : `${rawLineItemsAll.length} line items`}
                {costExact && <span className="ml-2 text-brand-600">(exact match — min/max ignored)</span>}
              </span>
              {(invoiceSearch || costExact || costMin || costMax) && (
                <span>Filtered total: <strong className="text-red-700">{fmt(filteredTotal)}</strong></span>
              )}
            </div>
          </div>
        )}

        <div className="text-right text-lg font-semibold">Grand Total: <span className="text-red-700">{fmt(grandTotal)}</span></div>

        {isLoading ? <Loading /> : view === "summary" ? (
          rollupSort.rows.length === 0 ? <EmptyState /> : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableTh col="entity_name" onSort={rollupSort.onSort} arrow={<rollupSort.SortArrow col="entity_name" />}>Vendor / Sub</SortableTh>
                    <SortableTh col="entity_type" onSort={rollupSort.onSort} arrow={<rollupSort.SortArrow col="entity_type" />}>Type</SortableTh>
                    <SortableTh col="item_count" onSort={rollupSort.onSort} arrow={<rollupSort.SortArrow col="item_count" />} align="right"># Items</SortableTh>
                    <SortableTh col="total_spent" onSort={rollupSort.onSort} arrow={<rollupSort.SortArrow col="total_spent" />} align="right">Total Spent</SortableTh>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rollupSort.rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <Td>{r.entity_name}</Td>
                      <Td><Badge variant="outline" className="text-xs">{r.entity_type}</Badge></Td>
                      <Td align="right">
                        <button
                          onClick={() => setDrilldown({ entity_name: r.entity_name, entity_type: r.entity_type, projects: r.projects || [] })}
                          className="text-brand-600 hover:text-brand-800 hover:underline font-medium"
                        >
                          {r.item_count}
                        </button>
                      </Td>
                      <Td align="right" className="font-semibold text-red-700">{fmt(r.total_spent)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          detailSort.rows.length === 0 ? <EmptyState /> : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableTh col="entity_name" onSort={detailSort.onSort} arrow={<detailSort.SortArrow col="entity_name" />}>Vendor/Sub</SortableTh>
                    <SortableTh col="entity_type" onSort={detailSort.onSort} arrow={<detailSort.SortArrow col="entity_type" />}>Type</SortableTh>
                    <SortableTh col="project_name" onSort={detailSort.onSort} arrow={<detailSort.SortArrow col="project_name" />}>Project</SortableTh>
                    <SortableTh col="kind" onSort={detailSort.onSort} arrow={<detailSort.SortArrow col="kind" />}>Kind</SortableTh>
                    <SortableTh col="item" onSort={detailSort.onSort} arrow={<detailSort.SortArrow col="item" />}>Item</SortableTh>
                    <SortableTh col="invoice_number" onSort={detailSort.onSort} arrow={<detailSort.SortArrow col="invoice_number" />}>Invoice #</SortableTh>
                    <SortableTh col="date" onSort={detailSort.onSort} arrow={<detailSort.SortArrow col="date" />}>Date</SortableTh>
                    <SortableTh col="cost" onSort={detailSort.onSort} arrow={<detailSort.SortArrow col="cost" />} align="right">Cost</SortableTh>
                    <Th align="right"></Th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {detailSort.rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <Td>{r.entity_name}</Td>
                      <Td className="text-xs">{r.entity_type}</Td>
                      <Td>{r.project_name}</Td>
                      <Td className="text-xs">{r.kind}</Td>
                      <Td>{r.item}</Td>
                      <Td className="text-xs">{r.invoice_number}</Td>
                      <Td className="text-xs">{r.date}</Td>
                      <Td align="right" className="text-red-700">{fmt(r.cost)}</Td>
                      <Td align="right">
                        <button
                          onClick={() => openEditItem(r)}
                          className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-brand-600"
                          title="Edit this line item"
                        >
                          Edit
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </CardContent>

      {/* Drill-down: projects this entity was used on */}
      <Dialog open={!!drilldown} onOpenChange={(open) => { if (!open) setDrilldown(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Projects: {drilldown?.entity_name}</DialogTitle>
            <DialogDescription>
              Every project where this {drilldown?.entity_type} appeared on a material or receipt
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden mt-2">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Project</Th>
                  <Th align="right"># Items</Th>
                  <Th align="right">Total Spent</Th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(drilldown?.projects || []).map((p) => (
                  <tr key={p.project_id} className="hover:bg-gray-50">
                    <Td>{p.project_name}</Td>
                    <Td align="right">{p.count}</Td>
                    <Td align="right" className="text-red-700 font-medium">{fmt(p.total)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit line item dialog */}
      <Dialog open={!!editingItem} onOpenChange={(open) => { if (!open) { setEditingItem(null); setEditForm(null); } }}>
        <DialogContent className="max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editingItem?.kind === 'material' ? 'Material' : 'Receipt'}</DialogTitle>
            <DialogDescription>Edit any field, or move this line item to a different project.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Project</Label>
                <select
                  value={editForm.target_project_id || ""}
                  onChange={(e) => setEditForm({ ...editForm, target_project_id: e.target.value })}
                  className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm"
                >
                  {((projectsList?.data) || [])
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((p) => (
                      <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>
                    ))}
                </select>
                {editForm.target_project_id !== editingItem?.project_id && (
                  <p className="text-xs text-amber-600 mt-1">This item will be MOVED to the selected project.</p>
                )}
              </div>

              <div>
                <Label className="text-xs">Item / Name</Label>
                <Input value={editForm.item_name} onChange={(e) => setEditForm({ ...editForm, item_name: e.target.value })} className="h-9" />
              </div>

              <div>
                <Label className="text-xs">Cost ($)</Label>
                <Input type="number" step="0.01" value={editForm.cost} onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })} className="h-9" />
              </div>

              <div>
                <Label className="text-xs">Vendor / Subcontractor / Designer</Label>
                <select
                  value={editForm.vendorValue || ""}
                  onChange={(e) => setEditForm({ ...editForm, vendorValue: e.target.value })}
                  className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">— None —</option>
                  {((vendorsList?.data) || []).length > 0 && <optgroup label="Vendors">
                    {((vendorsList?.data) || []).map((v) => (
                      <option key={`v-${v.id || v._id}`} value={`vendor:${v.id || v._id}`}>{v.company_name || v.name}</option>
                    ))}
                  </optgroup>}
                  {((subsList?.data) || []).length > 0 && <optgroup label="Subcontractors">
                    {((subsList?.data) || []).map((s) => (
                      <option key={`s-${s.id || s._id}`} value={`subcontractor:${s.id || s._id}`}>{s.name}</option>
                    ))}
                  </optgroup>}
                  {((designersList?.data) || []).length > 0 && <optgroup label="Designers">
                    {((designersList?.data) || []).map((d) => (
                      <option key={`d-${d.id || d._id}`} value={`designer:${d.id || d._id}`}>{d.name}</option>
                    ))}
                  </optgroup>}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Invoice #</Label>
                  <Input value={editForm.invoice_number} onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="h-9" />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setEditingItem(null); setEditForm(null); }}>Cancel</Button>
                <Button onClick={handleSaveItem} disabled={savingItem} className="bg-brand-600 hover:bg-brand-700">
                  {savingItem ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================================
// Expected Revenue (by start date or by end date)
// ============================================================================
function ExpectedRevenueReport() {
  const { toast } = useToast();
  const [dateField, setDateField] = useState("start_date");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["report-expected-revenue", dateField, dateFrom, dateTo],
    queryFn: () => detailedReportService.expectedRevenue({
      date_field: dateField,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
  });
  const rawRows = data?.data?.rows || [];
  const totals = data?.data?.totals || {};
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "date", "asc");

  const handleExport = async () => {
    setExporting(true);
    try {
      await detailedReportService.expectedRevenueExport({
        date_field: dateField,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
    } catch (e) {
      toast({ variant: "destructive", description: e.message });
    } finally {
      setExporting(false);
    }
  };

  const dateLabel = dateField === "start_date" ? "Start Date" : "Est. End Date";

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><CalendarRange className="h-5 w-5" /> Expected Revenue</CardTitle>
          <CardDescription>Projects with their {dateLabel.toLowerCase()} in the selected range, showing contract value and outstanding balance</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
          <div>
            <Label className="text-xs">Filter by</Label>
            <div className="flex gap-1 border rounded p-1 mt-1">
              <button
                onClick={() => setDateField("start_date")}
                className={`px-3 py-1 text-sm rounded ${dateField === "start_date" ? "bg-brand-600 text-white" : "hover:bg-gray-100"}`}
              >Start Date</button>
              <button
                onClick={() => setDateField("end_date")}
                className={`px-3 py-1 text-sm rounded ${dateField === "end_date" ? "bg-brand-600 text-white" : "hover:bg-gray-100"}`}
              >Est. End Date</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Projects" value={totals.project_count || 0} />
          <SummaryCard label="Total Contract" value={fmt(totals.total_contract)} />
          <SummaryCard label="Paid to Date" value={fmt(totals.total_paid)} color="text-green-600" />
          <SummaryCard label="Outstanding" value={fmt(totals.total_outstanding)} color="text-orange-600" />
        </div>

        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="status" onSort={onSort} arrow={<SortArrow col="status" />}>Status</SortableTh>
                  <SortableTh col="project_type" onSort={onSort} arrow={<SortArrow col="project_type" />}>Type</SortableTh>
                  <SortableTh col="date" onSort={onSort} arrow={<SortArrow col="date" />}>{dateLabel}</SortableTh>
                  <SortableTh col="contract_price" onSort={onSort} arrow={<SortArrow col="contract_price" />} align="right">Contract</SortableTh>
                  <SortableTh col="paid_to_date" onSort={onSort} arrow={<SortArrow col="paid_to_date" />} align="right">Paid</SortableTh>
                  <SortableTh col="outstanding" onSort={onSort} arrow={<SortArrow col="outstanding" />} align="right">Outstanding</SortableTh>
                  <SortableTh col="assigned_users" onSort={onSort} arrow={<SortArrow col="assigned_users" />}>Users</SortableTh>
                  <SortableTh col="crews" onSort={onSort} arrow={<SortArrow col="crews" />}>Crews/Subs</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.project_id} className="hover:bg-gray-50">
                    <Td>{r.project_name}</Td>
                    <Td>{r.customer_name}</Td>
                    <Td><Badge variant="outline" className="text-xs">{r.status}</Badge></Td>
                    <Td className="text-xs capitalize">{(r.project_type || '').replace('_', ' ')}</Td>
                    <Td className="text-xs">{r.date}</Td>
                    <Td align="right" className="font-medium">{fmt(r.contract_price)}</Td>
                    <Td align="right" className="text-green-700">{fmt(r.paid_to_date)}</Td>
                    <Td align="right" className="text-orange-700 font-semibold">{fmt(r.outstanding)}</Td>
                    <Td className="text-xs">{r.assigned_users || <span className="text-gray-400">—</span>}</Td>
                    <Td className="text-xs">{r.crews || <span className="text-gray-400">—</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 5. Lead Source ROI
// ============================================================================
function LeadSourceROIReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["report-lead-source-roi", dateFrom, dateTo],
    queryFn: () => detailedReportService.leadSourceROI({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const totals = data?.data?.totals || {};
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "total_revenue", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.leadSourceROIExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Lead Source ROI</CardTitle>
          <CardDescription>Conversion and revenue grouped by where the lead came from</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Total Leads" value={totals.total_leads || 0} />
          <SummaryCard label="Converted" value={totals.converted || 0} />
          <SummaryCard label="Revenue" value={fmt(totals.total_revenue)} color="text-green-600" />
          <SummaryCard label="Est. Pipeline" value={fmt(totals.total_estimated_value)} />
        </div>
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="source" onSort={onSort} arrow={<SortArrow col="source" />}>Source</SortableTh>
                  <SortableTh col="total_leads" onSort={onSort} arrow={<SortArrow col="total_leads" />} align="right">Leads</SortableTh>
                  <SortableTh col="converted" onSort={onSort} arrow={<SortArrow col="converted" />} align="right">Converted</SortableTh>
                  <SortableTh col="conversion_rate" onSort={onSort} arrow={<SortArrow col="conversion_rate" />} align="right">Conv. %</SortableTh>
                  <SortableTh col="total_revenue" onSort={onSort} arrow={<SortArrow col="total_revenue" />} align="right">Revenue</SortableTh>
                  <SortableTh col="total_estimated_value" onSort={onSort} arrow={<SortArrow col="total_estimated_value" />} align="right">Est. Pipeline</SortableTh>
                  <SortableTh col="revenue_per_lead" onSort={onSort} arrow={<SortArrow col="revenue_per_lead" />} align="right">$ / Lead</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td className="font-medium">{r.source}</Td>
                    <Td align="right">{r.total_leads}</Td>
                    <Td align="right">{r.converted}</Td>
                    <Td align="right">{fmtPct(r.conversion_rate)}</Td>
                    <Td align="right" className="text-green-700 font-medium">{fmt(r.total_revenue)}</Td>
                    <Td align="right">{fmt(r.total_estimated_value)}</Td>
                    <Td align="right">{fmt(r.revenue_per_lead)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 6. Change Orders
// ============================================================================
function ChangeOrdersReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-change-orders", dateFrom, dateTo],
    queryFn: () => detailedReportService.changeOrders({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const total = data?.data?.total || 0;
  const count = data?.data?.count || 0;
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "date", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.changeOrdersExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><FileEdit className="h-5 w-5" /> Change Orders</CardTitle>
          <CardDescription>Every change order across all projects</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
        <div className="grid grid-cols-2 gap-3">
          <SummaryCard label="Change Orders" value={count} />
          <SummaryCard label="Total Value" value={fmt(total)} color="text-orange-600" />
        </div>
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="date" onSort={onSort} arrow={<SortArrow col="date" />}>Date</SortableTh>
                  <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="scope_of_work" onSort={onSort} arrow={<SortArrow col="scope_of_work" />}>Scope</SortableTh>
                  <SortableTh col="amount" onSort={onSort} arrow={<SortArrow col="amount" />} align="right">Amount</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td>{r.date}</Td>
                    <Td>{r.project_name}</Td>
                    <Td>{r.customer_name}</Td>
                    <Td className="text-xs">{r.scope_of_work}</Td>
                    <Td align="right" className="font-medium text-orange-700">{fmt(r.amount)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 7. Sales Pipeline
// ============================================================================
function SalesPipelineReport() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-sales-pipeline"],
    queryFn: () => detailedReportService.salesPipeline(),
  });
  const rawRows = data?.data?.rows || [];
  const totals = data?.data?.totals || {};
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "sort_order", "asc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.salesPipelineExport(); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Workflow className="h-5 w-5" /> Sales Pipeline</CardTitle>
          <CardDescription>All active projects grouped by status, showing $ at each stage</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Projects" value={totals.count || 0} />
          <SummaryCard label="Total Contract" value={fmt(totals.total_contract)} />
          <SummaryCard label="Paid to Date" value={fmt(totals.total_paid)} color="text-green-600" />
          <SummaryCard label="Outstanding" value={fmt(totals.total_outstanding)} color="text-orange-600" />
        </div>
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="label" onSort={onSort} arrow={<SortArrow col="label" />}>Status</SortableTh>
                  <SortableTh col="count" onSort={onSort} arrow={<SortArrow col="count" />} align="right">Projects</SortableTh>
                  <SortableTh col="total_contract" onSort={onSort} arrow={<SortArrow col="total_contract" />} align="right">Total Contract</SortableTh>
                  <SortableTh col="total_paid" onSort={onSort} arrow={<SortArrow col="total_paid" />} align="right">Paid</SortableTh>
                  <SortableTh col="total_outstanding" onSort={onSort} arrow={<SortArrow col="total_outstanding" />} align="right">Outstanding</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td className="font-medium">{r.label}</Td>
                    <Td align="right">{r.count}</Td>
                    <Td align="right">{fmt(r.total_contract)}</Td>
                    <Td align="right" className="text-green-700">{fmt(r.total_paid)}</Td>
                    <Td align="right" className="text-orange-700 font-semibold">{fmt(r.total_outstanding)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 8. Customer LTV
// ============================================================================
function CustomerLTVReport() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-customer-ltv"],
    queryFn: () => detailedReportService.customerLTV(),
  });
  const rawRows = data?.data?.rows || [];
  const count = data?.data?.count || 0;
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "total_paid", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.customerLTVExport(); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  const totalRevenue = rows.reduce((s, r) => s + (r.total_paid || 0), 0);

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><UserCheck className="h-5 w-5" /> Customer Lifetime Value</CardTitle>
          <CardDescription>Total revenue per customer, ranked</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <SummaryCard label="Customers w/ Projects" value={count} />
          <SummaryCard label="Total Revenue" value={fmt(totalRevenue)} color="text-green-600" />
        </div>
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="email" onSort={onSort} arrow={<SortArrow col="email" />}>Email</SortableTh>
                  <SortableTh col="city" onSort={onSort} arrow={<SortArrow col="city" />}>City</SortableTh>
                  <SortableTh col="referral_source" onSort={onSort} arrow={<SortArrow col="referral_source" />}>Source</SortableTh>
                  <SortableTh col="project_count" onSort={onSort} arrow={<SortArrow col="project_count" />} align="right"># Projects</SortableTh>
                  <SortableTh col="total_contract" onSort={onSort} arrow={<SortArrow col="total_contract" />} align="right">Total Contract</SortableTh>
                  <SortableTh col="total_paid" onSort={onSort} arrow={<SortArrow col="total_paid" />} align="right">Paid</SortableTh>
                  <SortableTh col="outstanding" onSort={onSort} arrow={<SortArrow col="outstanding" />} align="right">Outstanding</SortableTh>
                  <SortableTh col="first_project_date" onSort={onSort} arrow={<SortArrow col="first_project_date" />}>First Project</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.customer_id} className="hover:bg-gray-50">
                    <Td className="font-medium">{r.customer_name}</Td>
                    <Td className="text-xs">{r.email}</Td>
                    <Td className="text-xs">{r.city}</Td>
                    <Td className="text-xs">{r.referral_source}</Td>
                    <Td align="right">{r.project_count}</Td>
                    <Td align="right">{fmt(r.total_contract)}</Td>
                    <Td align="right" className="text-green-700 font-semibold">{fmt(r.total_paid)}</Td>
                    <Td align="right" className="text-orange-700">{fmt(r.outstanding)}</Td>
                    <Td className="text-xs">{r.first_project_date}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 9. New Customer Acquisition
// ============================================================================
function CustomerAcquisitionReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-customer-acquisition", dateFrom, dateTo],
    queryFn: () => detailedReportService.customerAcquisition({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const bySource = data?.data?.by_source || [];
  const byMonth = data?.data?.by_month || [];
  const count = data?.data?.count || 0;
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "created_at", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.customerAcquisitionExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5" /> New Customer Acquisition</CardTitle>
          <CardDescription>New customers added in the selected period, grouped by source and by month</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
        <SummaryCard label="New Customers" value={count} />

        {/* Two side-by-side small tables for source + month */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border rounded-lg p-3">
            <div className="text-sm font-semibold mb-2">By Source</div>
            <table className="w-full text-sm">
              <thead><tr><Th>Source</Th><Th align="right">New</Th></tr></thead>
              <tbody className="divide-y">
                {bySource.map((r, i) => (
                  <tr key={i}><Td>{r.source}</Td><Td align="right">{r.count}</Td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border rounded-lg p-3">
            <div className="text-sm font-semibold mb-2">By Month</div>
            <table className="w-full text-sm">
              <thead><tr><Th>Year-Month</Th><Th align="right">New</Th></tr></thead>
              <tbody className="divide-y">
                {byMonth.map((r, i) => (
                  <tr key={i}><Td className="text-xs">{r.month}</Td><Td align="right">{r.count}</Td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail table */}
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="created_at" onSort={onSort} arrow={<SortArrow col="created_at" />}>Created</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Name</SortableTh>
                  <SortableTh col="email" onSort={onSort} arrow={<SortArrow col="email" />}>Email</SortableTh>
                  <SortableTh col="phone" onSort={onSort} arrow={<SortArrow col="phone" />}>Phone</SortableTh>
                  <SortableTh col="city" onSort={onSort} arrow={<SortArrow col="city" />}>City</SortableTh>
                  <SortableTh col="source" onSort={onSort} arrow={<SortArrow col="source" />}>Source</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td className="text-xs">{r.created_at}</Td>
                    <Td className="font-medium">{r.customer_name}</Td>
                    <Td className="text-xs">{r.email}</Td>
                    <Td className="text-xs">{r.phone}</Td>
                    <Td className="text-xs">{r.city}</Td>
                    <Td className="text-xs">{r.source}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 10. Project Duration
// ============================================================================
function ProjectDurationReport() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-project-duration"],
    queryFn: () => detailedReportService.projectDuration(),
  });
  const rawRows = data?.data?.rows || [];
  const summary = data?.data?.summary || {};
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "contract_sign_date", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.projectDurationExport(); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Timer className="h-5 w-5" /> Project Duration & Velocity</CardTitle>
          <CardDescription>How long projects actually take from contract to completion</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <SummaryCard label="Projects" value={summary.project_count || 0} />
          <SummaryCard label="Avg Lead Time" value={`${summary.avg_lead_time_days || 0} days`} />
          <SummaryCard label="Avg Build Duration" value={`${summary.avg_duration_days || 0} days`} />
        </div>
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="status" onSort={onSort} arrow={<SortArrow col="status" />}>Status</SortableTh>
                  <SortableTh col="project_type" onSort={onSort} arrow={<SortArrow col="project_type" />}>Type</SortableTh>
                  <SortableTh col="contract_sign_date" onSort={onSort} arrow={<SortArrow col="contract_sign_date" />}>Sign</SortableTh>
                  <SortableTh col="start_date" onSort={onSort} arrow={<SortArrow col="start_date" />}>Start</SortableTh>
                  <SortableTh col="end_date" onSort={onSort} arrow={<SortArrow col="end_date" />}>End</SortableTh>
                  <SortableTh col="contract_to_start_days" onSort={onSort} arrow={<SortArrow col="contract_to_start_days" />} align="right">Lead Time</SortableTh>
                  <SortableTh col="duration_days" onSort={onSort} arrow={<SortArrow col="duration_days" />} align="right">Duration</SortableTh>
                  <SortableTh col="total_days" onSort={onSort} arrow={<SortArrow col="total_days" />} align="right">Total</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.project_id} className="hover:bg-gray-50">
                    <Td className="font-medium">{r.project_name}</Td>
                    <Td>{r.customer_name}</Td>
                    <Td><Badge variant="outline" className="text-xs">{r.status}</Badge></Td>
                    <Td className="text-xs capitalize">{(r.project_type || '').replace('_', ' ')}</Td>
                    <Td className="text-xs">{r.contract_sign_date}</Td>
                    <Td className="text-xs">{r.start_date}</Td>
                    <Td className="text-xs">{r.end_date}</Td>
                    <Td align="right">{r.contract_to_start_days}</Td>
                    <Td align="right">{r.duration_days}</Td>
                    <Td align="right" className="font-semibold">{r.total_days}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 11. Callbacks Analysis
// ============================================================================
function CallbacksAnalysisReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-callbacks-analysis", dateFrom, dateTo],
    queryFn: () => detailedReportService.callbacksAnalysis({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const byUrgency = data?.data?.by_urgency || [];
  const summary = data?.data?.summary || {};
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "created_at", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.callbacksAnalysisExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  const urgencyColor = (u) => ({
    urgent: 'bg-red-600 text-white',
    high: 'bg-orange-500 text-white',
    normal: 'bg-yellow-500 text-white',
    low: 'bg-gray-400 text-white',
  })[u] || 'bg-gray-300 text-gray-800';

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><PhoneCall className="h-5 w-5" /> Callbacks Analysis</CardTitle>
          <CardDescription>Callbacks by urgency, resolution time, and crew hours spent</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard label="Callbacks" value={summary.count || 0} />
          <SummaryCard label="Open" value={summary.open_count || 0} color="text-orange-600" />
          <SummaryCard label="Resolved" value={summary.resolved_count || 0} color="text-green-600" />
          <SummaryCard label="Avg Resolution" value={`${summary.avg_resolution_days || 0} days`} />
          <SummaryCard label="Total Crew Hrs" value={(summary.total_hours || 0).toFixed(1)} />
        </div>
        {byUrgency.length > 0 && (
          <div className="border rounded-lg p-3">
            <div className="text-sm font-semibold mb-2">By Urgency</div>
            <table className="w-full text-sm">
              <thead><tr><Th>Urgency</Th><Th align="right">Count</Th><Th align="right">Avg Resolution Days</Th><Th align="right">Total Hours</Th></tr></thead>
              <tbody className="divide-y">
                {byUrgency.map((r, i) => (
                  <tr key={i}>
                    <Td><Badge className={`${urgencyColor(r.urgency)} capitalize`}>{r.urgency}</Badge></Td>
                    <Td align="right">{r.count}</Td>
                    <Td align="right">{r.avg_resolution_days}</Td>
                    <Td align="right">{(r.total_hours || 0).toFixed(1)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="created_at" onSort={onSort} arrow={<SortArrow col="created_at" />}>Created</SortableTh>
                  <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="urgency" onSort={onSort} arrow={<SortArrow col="urgency" />}>Urgency</SortableTh>
                  <SortableTh col="status" onSort={onSort} arrow={<SortArrow col="status" />}>Status</SortableTh>
                  <SortableTh col="issue_description" onSort={onSort} arrow={<SortArrow col="issue_description" />}>Issue</SortableTh>
                  <SortableTh col="resolution_days" onSort={onSort} arrow={<SortArrow col="resolution_days" />} align="right">Days to Resolve</SortableTh>
                  <SortableTh col="total_hours" onSort={onSort} arrow={<SortArrow col="total_hours" />} align="right">Crew Hrs</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.callback_id} className="hover:bg-gray-50">
                    <Td className="text-xs">{r.created_at}</Td>
                    <Td>{r.project_name}</Td>
                    <Td>{r.customer_name}</Td>
                    <Td><Badge className={`${urgencyColor(r.urgency)} capitalize text-xs`}>{r.urgency}</Badge></Td>
                    <Td><Badge variant="outline" className="text-xs capitalize">{r.status}</Badge></Td>
                    <Td className="text-xs max-w-md truncate">{r.issue_description}</Td>
                    <Td align="right">{r.resolution_days}</Td>
                    <Td align="right">{(r.total_hours || 0).toFixed(1)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 12. Conversion Funnel
// ============================================================================
function ConversionFunnelReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-conversion-funnel", dateFrom, dateTo],
    queryFn: () => detailedReportService.conversionFunnel({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const funnel = data?.data?.funnel || [];

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.conversionFunnelExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  const maxCount = Math.max(1, ...funnel.map(f => f.count || 0));

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Filter className="h-5 w-5" /> Conversion Funnel</CardTitle>
          <CardDescription>How leads flow through your sales process to becoming completed projects</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
        {isLoading ? <Loading /> : (
          <div className="space-y-3">
            {funnel.map((stage, i) => {
              const widthPct = (stage.count / maxCount) * 100;
              return (
                <div key={i} className="border rounded-lg p-3">
                  <div className="flex justify-between items-baseline mb-2">
                    <div className="font-medium">{stage.stage}</div>
                    <div className="text-2xl font-bold">{stage.count}</div>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-600 transition-all" style={{ width: `${widthPct}%` }}></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{stage.rate_from_prev !== null ? `${(stage.rate_from_prev * 100).toFixed(1)}% from previous stage` : ''}</span>
                    <span>{stage.rate_from_top !== null ? `${(stage.rate_from_top * 100).toFixed(1)}% of total leads` : ''}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 13. Crew Utilization
// ============================================================================
function CrewUtilizationReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-crew-utilization", dateFrom, dateTo],
    queryFn: () => detailedReportService.crewUtilization({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "callback_hours", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.crewUtilizationExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  const totalHours = rows.reduce((s, r) => s + (r.callback_hours || 0), 0);
  const totalPayroll = rows.reduce((s, r) => s + (r.callback_payroll_value || 0), 0);

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Hammer className="h-5 w-5" /> Crew Utilization</CardTitle>
          <CardDescription>Per-crew callback hours and project assignments. Date filter applies to callback hours only.</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <SummaryCard label="Crews" value={rows.length} />
          <SummaryCard label="Total Callback Hours" value={totalHours.toFixed(1)} />
          <SummaryCard label="Callback Payroll Value" value={fmt(totalPayroll)} color="text-orange-600" />
        </div>
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="crew_name" onSort={onSort} arrow={<SortArrow col="crew_name" />}>Crew</SortableTh>
                  <SortableTh col="is_hourly" onSort={onSort} arrow={<SortArrow col="is_hourly" />}>Type</SortableTh>
                  <SortableTh col="member_count" onSort={onSort} arrow={<SortArrow col="member_count" />} align="right">Members</SortableTh>
                  <SortableTh col="project_assignments" onSort={onSort} arrow={<SortArrow col="project_assignments" />} align="right"># Projects</SortableTh>
                  <SortableTh col="callback_count" onSort={onSort} arrow={<SortArrow col="callback_count" />} align="right"># Callbacks</SortableTh>
                  <SortableTh col="callback_hours" onSort={onSort} arrow={<SortArrow col="callback_hours" />} align="right">Callback Hours</SortableTh>
                  <SortableTh col="callback_payroll_value" onSort={onSort} arrow={<SortArrow col="callback_payroll_value" />} align="right">Payroll Value</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.crew_id} className="hover:bg-gray-50">
                    <Td className="font-medium">{r.crew_name}</Td>
                    <Td><Badge variant="outline" className="text-xs">{r.is_hourly ? "Hourly" : "Sub/Non-Hourly"}</Badge></Td>
                    <Td align="right">{r.member_count}</Td>
                    <Td align="right">{r.project_assignments}</Td>
                    <Td align="right">{r.callback_count}</Td>
                    <Td align="right" className="font-medium">{(r.callback_hours || 0).toFixed(1)}</Td>
                    <Td align="right" className="text-orange-700">{fmt(r.callback_payroll_value)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 14. User Activity
// ============================================================================
function UserActivityReport() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-user-activity", dateFrom, dateTo],
    queryFn: () => detailedReportService.userActivity({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "tasks_completed", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.userActivityExport({ date_from: dateFrom || undefined, date_to: dateTo || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" /> User Activity</CardTitle>
          <CardDescription>Per-user task and email activity. Project assignment count is current (not date-filtered).</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="name" onSort={onSort} arrow={<SortArrow col="name" />}>User</SortableTh>
                  <SortableTh col="email" onSort={onSort} arrow={<SortArrow col="email" />}>Email</SortableTh>
                  <SortableTh col="role" onSort={onSort} arrow={<SortArrow col="role" />}>Role</SortableTh>
                  <SortableTh col="projects_assigned" onSort={onSort} arrow={<SortArrow col="projects_assigned" />} align="right">Projects</SortableTh>
                  <SortableTh col="tasks_created" onSort={onSort} arrow={<SortArrow col="tasks_created" />} align="right">Tasks Created</SortableTh>
                  <SortableTh col="tasks_assigned" onSort={onSort} arrow={<SortArrow col="tasks_assigned" />} align="right">Tasks Assigned</SortableTh>
                  <SortableTh col="tasks_completed" onSort={onSort} arrow={<SortArrow col="tasks_completed" />} align="right">Tasks Completed</SortableTh>
                  <SortableTh col="emails_sent" onSort={onSort} arrow={<SortArrow col="emails_sent" />} align="right">Emails Sent</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.user_id} className="hover:bg-gray-50">
                    <Td className="font-medium">{r.name}</Td>
                    <Td className="text-xs">{r.email}</Td>
                    <Td><Badge variant="outline" className="text-xs capitalize">{r.role}</Badge></Td>
                    <Td align="right">{r.projects_assigned}</Td>
                    <Td align="right">{r.tasks_created}</Td>
                    <Td align="right">{r.tasks_assigned}</Td>
                    <Td align="right" className="font-semibold text-green-700">{r.tasks_completed}</Td>
                    <Td align="right" className="text-blue-700">{r.emails_sent}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 15. Jobs by Location (city / zip filter)
// ============================================================================
function JobsByLocationReport() {
  const { toast } = useToast();
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-jobs-by-location", city, zipCode],
    queryFn: () => detailedReportService.jobsByLocation({ city: city || undefined, zip_code: zipCode || undefined }),
  });
  const rawRows = data?.data?.rows || [];
  const byCity = data?.data?.by_city || [];
  const byZip = data?.data?.by_zip || [];
  const total = data?.data?.total_contract || 0;
  const count = data?.data?.count || 0;
  const { rows, onSort, SortArrow } = useSortedRows(rawRows, "city", "asc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.jobsByLocationExport({ city: city || undefined, zip_code: zipCode || undefined }); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" /> Jobs by Location</CardTitle>
          <CardDescription>Filter projects by city or ZIP code with summary rollups</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">City contains</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} className="h-9 w-56" placeholder="e.g. Roseville" />
          </div>
          <div>
            <Label className="text-xs">Zip code (exact)</Label>
            <Input value={zipCode} onChange={(e) => setZipCode(e.target.value)} className="h-9 w-32" placeholder="e.g. 95746" />
          </div>
          {(city || zipCode) && (
            <Button variant="ghost" size="sm" onClick={() => { setCity(""); setZipCode(""); }}>Clear</Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SummaryCard label="Projects" value={count} />
          <SummaryCard label="Total Contract" value={fmt(total)} />
        </div>

        {/* Side-by-side rollups */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border rounded-lg p-3">
            <div className="text-sm font-semibold mb-2">By City</div>
            <table className="w-full text-sm">
              <thead><tr><Th>City</Th><Th align="right"># Projects</Th><Th align="right">Total Contract</Th></tr></thead>
              <tbody className="divide-y">
                {byCity.map((r, i) => (
                  <tr key={i}><Td>{r.city}</Td><Td align="right">{r.count}</Td><Td align="right">{fmt(r.total_contract)}</Td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border rounded-lg p-3">
            <div className="text-sm font-semibold mb-2">By Zip</div>
            <table className="w-full text-sm">
              <thead><tr><Th>Zip</Th><Th align="right"># Projects</Th><Th align="right">Total Contract</Th></tr></thead>
              <tbody className="divide-y">
                {byZip.map((r, i) => (
                  <tr key={i}><Td>{r.zip}</Td><Td align="right">{r.count}</Td><Td align="right">{fmt(r.total_contract)}</Td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {isLoading ? <Loading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                  <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                  <SortableTh col="jobsite_address" onSort={onSort} arrow={<SortArrow col="jobsite_address" />}>Address</SortableTh>
                  <SortableTh col="city" onSort={onSort} arrow={<SortArrow col="city" />}>City</SortableTh>
                  <SortableTh col="zip" onSort={onSort} arrow={<SortArrow col="zip" />}>Zip</SortableTh>
                  <SortableTh col="status" onSort={onSort} arrow={<SortArrow col="status" />}>Status</SortableTh>
                  <SortableTh col="contract_price" onSort={onSort} arrow={<SortArrow col="contract_price" />} align="right">Contract</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.project_id} className="hover:bg-gray-50">
                    <Td className="font-medium">{r.project_name}</Td>
                    <Td>{r.customer_name}</Td>
                    <Td className="text-xs">{r.jobsite_address}</Td>
                    <Td>{r.city}</Td>
                    <Td>{r.zip}</Td>
                    <Td><Badge variant="outline" className="text-xs">{r.status}</Badge></Td>
                    <Td align="right" className="font-medium">{fmt(r.contract_price)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 16. Proximity Search
// ============================================================================
function ProximitySearchReport() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [clearing, setClearing] = useState(false);
  const [originMode, setOriginMode] = useState("address"); // 'address' or 'entity'
  const [address, setAddress] = useState("");
  const [originEntityType, setOriginEntityType] = useState("customer");
  const [originEntityId, setOriginEntityId] = useState("");
  const [originEntityLabel, setOriginEntityLabel] = useState("");

  const [committed, setCommitted] = useState(null); // search params committed on click
  const [radius, setRadius] = useState(10);
  const [entity, setEntity] = useState("project");
  const [exporting, setExporting] = useState(false);

  // Optional filters on results
  const [filterCity, setFilterCity] = useState("");
  const [filterZip, setFilterZip] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [contractMin, setContractMin] = useState("");
  const [contractMax, setContractMax] = useState("");

  const buildParams = () => ({
    radius_miles: radius,
    entity,
    ...(committed?.address ? { address: committed.address } : {}),
    ...(committed?.origin_entity_id ? { origin_entity_type: committed.origin_entity_type, origin_entity_id: committed.origin_entity_id } : {}),
    ...(filterCity ? { city: filterCity } : {}),
    ...(filterZip ? { zip: filterZip } : {}),
    ...(filterStatus ? { status: filterStatus } : {}),
    ...(contractMin ? { contract_min: contractMin } : {}),
    ...(contractMax ? { contract_max: contractMax } : {}),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-proximity", committed, radius, entity, filterCity, filterZip, filterStatus, contractMin, contractMax],
    queryFn: () => detailedReportService.proximitySearch(buildParams()),
    enabled: !!committed,
  });
  const results = data?.data?.results || [];
  const origin = data?.data?.origin;
  const { rows, onSort, SortArrow } = useSortedRows(results, "distance_miles", "asc");

  const handleSearch = () => {
    if (originMode === "address") {
      if (!address.trim()) {
        toast({ variant: "destructive", description: "Enter an address to search from" });
        return;
      }
      setCommitted({ address: address.trim() });
    } else {
      if (!originEntityId) {
        toast({ variant: "destructive", description: "Select an existing record to search from" });
        return;
      }
      setCommitted({ origin_entity_type: originEntityType, origin_entity_id: originEntityId });
    }
  };

  const handleExport = async () => {
    if (!committed) return;
    setExporting(true);
    try { await detailedReportService.proximitySearchExport(buildParams()); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  const handleClearFailed = async () => {
    setClearing(true);
    try {
      const resp = await detailedReportService.clearFailedGeocodes();
      const msg = resp?.data?.message || resp?.message || "Cleared.";
      toast({ description: msg });
      queryClient.invalidateQueries({ queryKey: ["report-proximity"] });
    } catch (e) {
      toast({ variant: "destructive", description: e.message });
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Search className="h-5 w-5" /> Proximity Search</CardTitle>
          <CardDescription>Find every project, customer, or lead within a radius of any address. Geocoded addresses are cached after the first search.</CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <GeocodingStatusBanner />

        {/* Origin: typed address OR existing record */}
        <div className="border rounded-lg p-3 bg-gray-50">
          <div className="flex gap-1 mb-3">
            <button onClick={() => setOriginMode("address")} className={`px-3 py-1 text-sm rounded ${originMode === "address" ? "bg-brand-600 text-white" : "bg-white border"}`}>From an address</button>
            <button onClick={() => setOriginMode("entity")} className={`px-3 py-1 text-sm rounded ${originMode === "entity" ? "bg-brand-600 text-white" : "bg-white border"}`}>From existing record</button>
          </div>
          {originMode === "address" ? (
            <div>
              <Label className="text-xs">Search from address</Label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                placeholder="e.g. 116 Dempsey Court, Roseville, CA 95661"
                className="h-9"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Record type</Label>
                <select value={originEntityType} onChange={(e) => { setOriginEntityType(e.target.value); setOriginEntityId(""); setOriginEntityLabel(""); }} className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm">
                  <option value="customer">Customer</option>
                  <option value="project">Project</option>
                  <option value="lead">Lead</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Pick {originEntityType}</Label>
                <EntityPicker entityType={originEntityType} valueId={originEntityId} valueLabel={originEntityLabel} onChange={(id, label) => { setOriginEntityId(id); setOriginEntityLabel(label); }} />
              </div>
            </div>
          )}
        </div>

        {/* Radius + entity + filters */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Radius (miles)</Label>
            <Input type="number" min="0.1" step="0.5" max="200" value={radius} onChange={(e) => setRadius(parseFloat(e.target.value) || 10)} className="h-9" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Find</Label>
            <select value={entity} onChange={(e) => setEntity(e.target.value)} className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm">
              <option value="project">Projects</option>
              <option value="customer">Customers</option>
              <option value="lead">Leads</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">City contains</Label>
            <Input value={filterCity} onChange={(e) => setFilterCity(e.target.value)} className="h-9" placeholder="optional" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Zip (exact)</Label>
            <Input value={filterZip} onChange={(e) => setFilterZip(e.target.value)} className="h-9" placeholder="optional" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Status</Label>
            <StatusDropdown entityType={entity} value={filterStatus} onChange={setFilterStatus} />
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button onClick={handleSearch} className="bg-brand-600 hover:bg-brand-700 w-full">
              <Search className="h-4 w-4 mr-2" />Search
            </Button>
          </div>
          {entity === "project" && (
            <>
              <div className="md:col-span-3">
                <Label className="text-xs">Min contract $</Label>
                <Input type="number" value={contractMin} onChange={(e) => setContractMin(e.target.value)} className="h-9" placeholder="e.g. 25000" />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Max contract $</Label>
                <Input type="number" value={contractMax} onChange={(e) => setContractMax(e.target.value)} className="h-9" placeholder="e.g. 100000" />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={handleClearFailed} disabled={clearing}>
            {clearing ? "Retrying..." : "Retry failed geocodes"}
          </Button>
        </div>

        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            <div className="font-semibold">Search failed</div>
            <div>{error?.detail || error?.response?.data?.detail || error?.message || "Unknown error"}</div>
            <div className="text-xs mt-1">Tip: if you recently fixed your API key, click "Retry failed geocodes" above.</div>
          </div>
        )}

        {committed && (
          <div className="text-sm text-gray-600">
            Searching within <strong>{radius} miles</strong> of <strong>{origin?.label || origin?.address || (committed?.address || originEntityLabel)}</strong>
            {origin && <span className="text-xs text-gray-400 ml-2">({origin.lat.toFixed(4)}, {origin.lng.toFixed(4)})</span>}
          </div>
        )}

        {isLoading ? <Loading /> : !committed ? (
          <div className="text-center py-12 text-gray-500">
            <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
            Pick an origin above and click Search
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No {entity}s found within {radius} miles</div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="distance_miles" onSort={onSort} arrow={<SortArrow col="distance_miles" />} align="right">Distance (mi)</SortableTh>
                  <SortableTh col="name" onSort={onSort} arrow={<SortArrow col="name" />}>Name</SortableTh>
                  {entity === "project" && <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>}
                  {entity !== "project" && <SortableTh col="email" onSort={onSort} arrow={<SortArrow col="email" />}>Email</SortableTh>}
                  <SortableTh col="address" onSort={onSort} arrow={<SortArrow col="address" />}>Address</SortableTh>
                  {entity !== "customer" && <SortableTh col="status" onSort={onSort} arrow={<SortArrow col="status" />}>Status</SortableTh>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <Td align="right" className="font-semibold">{r.distance_miles}</Td>
                    <Td className="font-medium">{r.name}</Td>
                    {entity === "project" && <Td>{r.customer_name}</Td>}
                    {entity !== "project" && <Td className="text-xs">{r.email}</Td>}
                    <Td className="text-xs">{r.address}</Td>
                    {entity !== "customer" && <Td><Badge variant="outline" className="text-xs">{r.status}</Badge></Td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 17. Map View — plot leads, customers, or projects
// ============================================================================
function MapViewReport() {
  const { toast } = useToast();
  const [entity, setEntity] = useState("project");
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Filters
  const [filterCity, setFilterCity] = useState("");
  const [filterZip, setFilterZip] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterProjectType, setFilterProjectType] = useState("");
  const [contractMin, setContractMin] = useState("");
  const [contractMax, setContractMax] = useState("");

  // Radius filter (optional)
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState(10);
  const [originMode, setOriginMode] = useState("address"); // 'address' or 'entity'
  const [originAddress, setOriginAddress] = useState("");
  const [originEntityType, setOriginEntityType] = useState("customer");
  const [originEntityId, setOriginEntityId] = useState("");
  const [originEntityLabel, setOriginEntityLabel] = useState("");

  // Clear-failed-cache action
  const [clearing, setClearing] = useState(false);
  const queryClient = useQueryClient();

  const params = {
    entity,
    ...(filterCity ? { city: filterCity } : {}),
    ...(filterZip ? { zip: filterZip } : {}),
    ...(filterStatus ? { status: filterStatus } : {}),
    ...(filterSource ? { source: filterSource } : {}),
    ...(filterProjectType ? { project_type: filterProjectType } : {}),
    ...(contractMin ? { contract_min: contractMin } : {}),
    ...(contractMax ? { contract_max: contractMax } : {}),
    ...(radiusEnabled && radiusMiles ? { radius_miles: radiusMiles } : {}),
    ...(radiusEnabled && originMode === "address" && originAddress ? { origin_address: originAddress } : {}),
    ...(radiusEnabled && originMode === "entity" && originEntityId ? { origin_entity_type: originEntityType, origin_entity_id: originEntityId } : {}),
  };

  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const markersRef = React.useRef([]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["report-map-data", JSON.stringify(params)],
    queryFn: () => detailedReportService.mapData(params),
  });
  const pins = data?.data?.pins || [];
  const plotted = data?.data?.plotted || 0;
  const unplottable = data?.data?.unplottable || 0;

  const handleClearFailed = async () => {
    setClearing(true);
    try {
      const resp = await detailedReportService.clearFailedGeocodes();
      const msg = resp?.data?.message || resp?.message || "Cleared.";
      toast({ description: msg });
      queryClient.invalidateQueries({ queryKey: ["report-map-data"] });
      refetch();
    } catch (e) {
      toast({ variant: "destructive", description: e.message });
    } finally {
      setClearing(false);
    }
  };

  // Initialize map once
  React.useEffect(() => {
    if (mapInstanceRef.current) return;
    const init = () => {
      if (!window.google?.maps || !mapRef.current) {
        setTimeout(init, 100);
        return;
      }
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 38.7521, lng: -121.2880 }, // Roseville-ish default
        zoom: 10,
      });
    };
    init();
  }, []);

  // Render pins whenever data changes
  React.useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.google?.maps) return;

    // Clear old markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (pins.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    const infoWindow = new window.google.maps.InfoWindow();

    const colorByEntity = { project: "#10b981", customer: "#3b82f6", lead: "#f59e0b" };
    const fill = colorByEntity[entity] || "#3b82f6";

    pins.forEach((pin) => {
      const marker = new window.google.maps.Marker({
        position: { lat: pin.lat, lng: pin.lng },
        map,
        title: pin.title,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: fill,
          fillOpacity: 0.85,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          scale: 8,
        },
      });
      marker.addListener("click", () => {
        infoWindow.setContent(`
          <div style="max-width: 240px; font-family: system-ui;">
            <div style="font-weight: 600; margin-bottom: 4px;">${pin.title}</div>
            ${pin.subtitle ? `<div style="font-size: 12px; color: #666;">${pin.subtitle}</div>` : ''}
            <div style="font-size: 11px; color: #888; margin-top: 6px;">${pin.address}</div>
            ${pin.status ? `<div style="margin-top: 6px;"><span style="background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-size: 11px;">${pin.status}</span></div>` : ''}
          </div>
        `);
        infoWindow.open({ anchor: marker, map });
      });
      markersRef.current.push(marker);
      bounds.extend({ lat: pin.lat, lng: pin.lng });
    });

    if (pins.length > 1) {
      map.fitBounds(bounds, 50);
    } else if (pins.length === 1) {
      map.setCenter({ lat: pins[0].lat, lng: pins[0].lng });
      map.setZoom(13);
    }
  }, [pins, entity]);

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" /> Map View</CardTitle>
          <CardDescription>Plot every project, customer, or lead. Pins are geocoded on first load and cached.</CardDescription>
        </div>
        <div className="flex gap-1 border rounded p-1">
          <button onClick={() => setEntity("project")} className={`px-3 py-1 text-sm rounded ${entity === "project" ? "bg-emerald-600 text-white" : "hover:bg-gray-100"}`}>Projects</button>
          <button onClick={() => setEntity("customer")} className={`px-3 py-1 text-sm rounded ${entity === "customer" ? "bg-blue-600 text-white" : "hover:bg-gray-100"}`}>Customers</button>
          <button onClick={() => setEntity("lead")} className={`px-3 py-1 text-sm rounded ${entity === "lead" ? "bg-amber-500 text-white" : "hover:bg-gray-100"}`}>Leads</button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <GeocodingStatusBanner />

        {/* Filters panel */}
        <div className="border rounded-lg">
          <button onClick={() => setFiltersOpen(!filtersOpen)} className="w-full px-3 py-2 flex items-center justify-between text-sm font-semibold hover:bg-gray-50">
            <span className="flex items-center gap-2"><Filter className="h-4 w-4" /> Filters</span>
            <span className="text-xs text-gray-500">{filtersOpen ? "Hide" : "Show"}</span>
          </button>
          {filtersOpen && (
            <div className="p-3 border-t space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">City contains</Label>
                  <Input value={filterCity} onChange={(e) => setFilterCity(e.target.value)} className="h-9" placeholder="e.g. Roseville" />
                </div>
                <div>
                  <Label className="text-xs">Zip (exact)</Label>
                  <Input value={filterZip} onChange={(e) => setFilterZip(e.target.value)} className="h-9" placeholder="e.g. 95746" />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <StatusDropdown entityType={entity} value={filterStatus} onChange={setFilterStatus} />
                </div>
                {entity !== "project" && (
                  <div>
                    <Label className="text-xs">Source/Referral (exact)</Label>
                    <Input value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="h-9" placeholder="e.g. Google" />
                  </div>
                )}
                {entity === "project" && (
                  <>
                    <div>
                      <Label className="text-xs">Project type</Label>
                      <select value={filterProjectType} onChange={(e) => setFilterProjectType(e.target.value)} className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm">
                        <option value="">Any</option>
                        <option value="construction">Construction</option>
                        <option value="design_only">Design only</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Min contract $</Label>
                      <Input type="number" value={contractMin} onChange={(e) => setContractMin(e.target.value)} className="h-9" placeholder="e.g. 25000" />
                    </div>
                    <div>
                      <Label className="text-xs">Max contract $</Label>
                      <Input type="number" value={contractMax} onChange={(e) => setContractMax(e.target.value)} className="h-9" placeholder="e.g. 100000" />
                    </div>
                  </>
                )}
              </div>

              {/* Radius filter */}
              <div className="border-t pt-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={radiusEnabled} onChange={(e) => setRadiusEnabled(e.target.checked)} />
                  Limit to within a radius
                </label>
                {radiusEnabled && (
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-2">
                      <Label className="text-xs">Radius (miles)</Label>
                      <Input type="number" min="0.1" step="0.5" max="200" value={radiusMiles} onChange={(e) => setRadiusMiles(parseFloat(e.target.value) || 10)} className="h-9" />
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">Origin type</Label>
                      <div className="flex gap-1 border rounded p-1">
                        <button onClick={() => setOriginMode("address")} className={`flex-1 px-2 py-1 text-sm rounded ${originMode === "address" ? "bg-brand-600 text-white" : ""}`}>Address</button>
                        <button onClick={() => setOriginMode("entity")} className={`flex-1 px-2 py-1 text-sm rounded ${originMode === "entity" ? "bg-brand-600 text-white" : ""}`}>Existing record</button>
                      </div>
                    </div>
                    {originMode === "address" ? (
                      <div className="md:col-span-7">
                        <Label className="text-xs">Origin address</Label>
                        <Input value={originAddress} onChange={(e) => setOriginAddress(e.target.value)} className="h-9" placeholder="e.g. 116 Dempsey Ct, Roseville, CA" />
                      </div>
                    ) : (
                      <>
                        <div className="md:col-span-3">
                          <Label className="text-xs">Record type</Label>
                          <select value={originEntityType} onChange={(e) => { setOriginEntityType(e.target.value); setOriginEntityId(""); setOriginEntityLabel(""); }} className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm">
                            <option value="customer">Customer</option>
                            <option value="project">Project</option>
                            <option value="lead">Lead</option>
                          </select>
                        </div>
                        <div className="md:col-span-4">
                          <Label className="text-xs">Pick {originEntityType}</Label>
                          <EntityPicker entityType={originEntityType} valueId={originEntityId} valueLabel={originEntityLabel} onChange={(id, label) => { setOriginEntityId(id); setOriginEntityLabel(label); }} />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="ghost" size="sm" onClick={() => { setFilterCity(""); setFilterZip(""); setFilterStatus(""); setFilterSource(""); setFilterProjectType(""); setContractMin(""); setContractMax(""); setRadiusEnabled(false); setOriginAddress(""); setOriginEntityId(""); }}>
                  Clear all filters
                </Button>
                <Button variant="outline" size="sm" onClick={handleClearFailed} disabled={clearing}>
                  {clearing ? "Retrying..." : "Retry failed geocodes"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <SummaryCard label={`${entity}s plotted`} value={plotted} color="text-green-600" />
          <SummaryCard label="Could not plot (no address or geocode failed)" value={unplottable} color={unplottable > 0 ? "text-orange-600" : "text-gray-500"} />
        </div>
        {isLoading && <Loading />}
        <div ref={mapRef} className="w-full rounded-lg border" style={{ height: "600px" }} />
        {!window.google?.maps && (
          <div className="text-xs text-amber-600">Google Maps script isn't loaded. Address autocomplete loads it automatically — try refreshing the page.</div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Payment Audit
// ============================================================================
function PaymentAuditReport() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["report-payment-audit"],
    queryFn: () => detailedReportService.paymentAudit(),
  });
  const mismatches = data?.data?.mismatches || [];
  const noReceipt = data?.data?.no_receipt || [];
  const mismatchCount = data?.data?.mismatch_count || 0;
  const noReceiptCount = data?.data?.no_receipt_count || 0;
  const projectsAudited = data?.data?.projects_audited || 0;
  const { rows, onSort, SortArrow } = useSortedRows(mismatches, "delta", "desc");

  const handleExport = async () => {
    setExporting(true);
    try { await detailedReportService.paymentAuditExport(); }
    catch (e) { toast({ variant: "destructive", description: e.message }); }
    finally { setExporting(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-600" /> Payment Audit</CardTitle>
          <CardDescription>
            Compares each paid payment's current amount against the amount in the receipt email that was sent for it.
            Mismatches indicate the payment was modified AFTER the receipt was sent (e.g. by the change-order bug).
          </CardDescription>
        </div>
        <ExportButton onClick={handleExport} loading={exporting} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <SummaryCard label="Projects audited" value={projectsAudited} />
          <SummaryCard label="Mismatched payments" value={mismatchCount} color={mismatchCount > 0 ? "text-red-600" : "text-green-600"} />
          <SummaryCard label="No receipt found" value={noReceiptCount} color="text-gray-500" />
        </div>

        {isLoading ? <Loading /> : mismatchCount === 0 ? (
          <div className="rounded-lg border border-green-300 bg-green-50 p-4 text-center text-sm">
            <div className="font-semibold text-green-900">No mismatches found</div>
            <div className="text-green-800 mt-1">Every paid payment matches the amount on the receipt that was sent. Audit covered {projectsAudited} project(s) with change orders.</div>
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm">
              <div className="font-semibold text-red-900">⚠️ {mismatchCount} payment(s) need repair</div>
              <div className="text-red-800 mt-1">
                For each row below: edit the payment in the project, set Amount to the <strong>Receipt Amount</strong> (what the customer actually paid), then save. The Final Payment will recalculate accordingly.
              </div>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableTh col="project_name" onSort={onSort} arrow={<SortArrow col="project_name" />}>Project</SortableTh>
                    <SortableTh col="customer_name" onSort={onSort} arrow={<SortArrow col="customer_name" />}>Customer</SortableTh>
                    <SortableTh col="payment_number" onSort={onSort} arrow={<SortArrow col="payment_number" />} align="right">Pmt #</SortableTh>
                    <SortableTh col="description" onSort={onSort} arrow={<SortArrow col="description" />}>Description</SortableTh>
                    <SortableTh col="receipt_amount" onSort={onSort} arrow={<SortArrow col="receipt_amount" />} align="right">Receipt Amount</SortableTh>
                    <SortableTh col="current_amount" onSort={onSort} arrow={<SortArrow col="current_amount" />} align="right">Current Amount</SortableTh>
                    <SortableTh col="delta" onSort={onSort} arrow={<SortArrow col="delta" />} align="right">Delta</SortableTh>
                    <SortableTh col="receipt_sent_at" onSort={onSort} arrow={<SortArrow col="receipt_sent_at" />}>Receipt Sent</SortableTh>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <Td className="font-medium">{r.project_name}</Td>
                      <Td>{r.customer_name}</Td>
                      <Td align="right">{r.payment_number}</Td>
                      <Td className="text-xs">{r.description}</Td>
                      <Td align="right" className="font-medium text-green-700">{fmt(r.receipt_amount)}</Td>
                      <Td align="right" className="text-red-700">{fmt(r.current_amount)}</Td>
                      <Td align="right" className={r.delta > 0 ? "text-red-700 font-semibold" : "text-orange-600 font-semibold"}>{r.delta > 0 ? "+" : ""}{fmt(r.delta)}</Td>
                      <Td className="text-xs">{r.receipt_sent_at}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {noReceiptCount > 0 && (
          <details className="border rounded-lg p-3">
            <summary className="cursor-pointer text-sm font-semibold">
              {noReceiptCount} paid payment(s) have no matching receipt email — can't audit these
            </summary>
            <div className="mt-2 text-xs text-gray-600 mb-2">
              These were either paid before the receipt-tracking feature existed, or the receipt was sent through a different channel. Skipped because we have nothing to compare against.
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr><Th>Project</Th><Th>Customer</Th><Th align="right">Pmt #</Th><Th>Description</Th><Th align="right">Current Amount</Th></tr>
              </thead>
              <tbody className="divide-y">
                {noReceipt.map((r, i) => (
                  <tr key={i}>
                    <Td>{r.project_name}</Td>
                    <Td>{r.customer_name}</Td>
                    <Td align="right">{r.payment_number}</Td>
                    <Td className="text-xs">{r.description}</Td>
                    <Td align="right">{fmt(r.current_amount)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Small UI primitives
// ============================================================================
function Th({ children, align = "left" }) {
  return <th className={`px-3 py-2 text-${align} text-xs font-semibold uppercase text-gray-500`}>{children}</th>;
}
function SortableTh({ children, col, onSort, arrow, align = "left" }) {
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-2 text-${align} text-xs font-semibold uppercase text-gray-500 cursor-pointer select-none hover:bg-gray-100`}
    >
      {children}{arrow}
    </th>
  );
}
function Td({ children, align = "left", className = "" }) {
  return <td className={`px-3 py-2 text-${align} ${className}`}>{children}</td>;
}
function SummaryCard({ label, value, color = "" }) {
  return (
    <div className="border rounded-lg p-3 bg-gray-50">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
function Loading() { return <div className="text-center py-8 text-gray-500">Loading...</div>; }
function EmptyState() { return <div className="text-center py-8 text-gray-500"><FileSpreadsheet className="h-12 w-12 mx-auto mb-2 opacity-30" />No data for the selected period</div>; }

// Status dropdown that fetches the available options for the given entity type
function StatusDropdown({ entityType, value, onChange, className = "" }) {
  const { data } = useQuery({
    queryKey: ["status-options", entityType],
    queryFn: () => detailedReportService.statusOptions(entityType),
    staleTime: 5 * 60 * 1000,
  });
  const options = data?.data?.options || [];
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`w-full h-9 px-3 border border-gray-300 rounded-md text-sm ${className}`}>
      <option value="">Any status</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// Lightweight typeahead that fetches from one of the existing services and returns id+label
function EntityPicker({ entityType, valueId, valueLabel, onChange }) {
  const [search, setSearch] = useState(valueLabel || "");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || search.length < 2) { setItems([]); return; }
    let cancelled = false;
    setLoading(true);
    const fetcher = async () => {
      try {
        const path = entityType === "customer" ? "/customers/" : entityType === "lead" ? "/leads/" : "/projects/";
        const resp = await fetch(`${api.defaults.baseURL || ''}${path}?search=${encodeURIComponent(search)}&limit=10`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        });
        const data = await resp.json();
        if (cancelled) return;
        const list = data?.data || [];
        setItems(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("EntityPicker fetch failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = setTimeout(fetcher, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, entityType, open]);

  const labelOf = (it) => entityType === "project"
    ? (it.name || "Untitled project")
    : `${it.name || ""} ${it.last_name || ""}`.trim() || "Unknown";

  return (
    <div className="relative">
      <Input
        value={search}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); if (!e.target.value) onChange("", ""); }}
        onFocus={() => setOpen(true)}
        placeholder={`Type to search ${entityType}s...`}
        className="h-9"
      />
      {open && search.length >= 2 && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
            ) : items.map((it) => {
              const id = it.id || it._id;
              const label = labelOf(it);
              return (
                <button key={id} type="button" onClick={() => { onChange(id, label); setSearch(label); setOpen(false); }} className="w-full px-3 py-2 text-left hover:bg-gray-100 text-sm">
                  <div className="font-medium">{label}</div>
                  <div className="text-xs text-gray-500">{it.jobsite_address || it.address || it.email || ""}</div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function GeocodingStatusBanner() {
  const { data } = useQuery({
    queryKey: ["geocoding-status"],
    queryFn: () => detailedReportService.geocodingStatus(),
    staleTime: 5 * 60 * 1000,
  });
  const status = data?.data;
  if (!status || status.ok) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
      <div className="font-semibold text-amber-900 flex items-center gap-2">
        <AlertCircle className="h-4 w-4" /> Geocoding not working
      </div>
      <div className="text-amber-800 mt-1">{status.message}</div>
      {status.key_suffix && (
        <div className="text-xs text-amber-700 mt-1">Key in use: ending in <code>{status.key_suffix}</code></div>
      )}
    </div>
  );
}
